import classNames from 'classnames';
import React, { useEffect } from 'react';
import Table from '../../atoms/Table';
import Modal from '../../molecules/Modal';
import Button from '../../atoms/Button';
import CircularProgress from '../../atoms/CircularProgress';
import { HearingInfo } from '../../../external/HearingInfoApi';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from '../../../redux/modules/store';
import { formatDateNull } from '../../../Common';
import { clearHearingInfoAll, fetchHearingInfoAll } from '../../../redux/operations/HearingInfo';
import { GASpotEventState } from '../NegotiationCountList';

export interface HearingResponseInfoProps {
  targetDate: Date;
  targetStore: string;
  tableBodyClassName?: string;
  gaSpotEvent: {
    [key: string]: GASpotEventState;
  };
}

const HearingResponseInfoModal: React.FC<HearingResponseInfoProps> = ({
  targetDate,
  targetStore,
  tableBodyClassName = '',
  gaSpotEvent,
}) => {
  const { isOpen, isLoading, visitReservationInfo, hearingInfoList } = useSelector((state: AppState) => state.hearingInfo);
  const { firstInsDate, code, hearingId } = visitReservationInfo;
  const dispatch = useDispatch();

  const onCloseModal = () => {
    clearHearingInfoAll(dispatch);
  };

  useEffect(() => {
    if (!isOpen || !hearingId) return;
    fetchHearingInfoAll(hearingId, targetDate, targetStore, dispatch);
  }, [isOpen, hearingId, targetDate, targetStore]);

  if (!isOpen) return <></>;

  return (
    <Modal
      open
      hasFade={false}
      onBackdropClick={onCloseModal}
      onEscapeKeyDown={onCloseModal}
      className='uc-modal__hearing-info-result'
      surfaceClassName='u-mv-auto'>
      <ModalHeader code={code} firstInsDate={firstInsDate} gaSpotEventText={gaSpotEvent.close.spotEvent} onClickCloseModal={onCloseModal} />
      <Modal.Content className='u-p16 u-pt0 content-action__table u-p0'>
        <hr className='uc-divider' />
        <div className='info-answer u-text-s' data-testid='info-answer' data-testid-e2e='info-answer'>
          ヒアリング回答内容
        </div>
        <Table className='hearing-info-result-table u-flex-1 u-overflow-hidden' isScrollable={true} height='s'>
          <Table.THead>
            <Table.Tr className='hearing-info-result-table__header-row'>
              <Table.Th
                data-testid='hearingInfoResultThead-question'
                data-testid-e2e='hearingInfoResultThead-question'
                className='u-bg-gray-alt hearing-info-result__question'
                key='設問'>
                設問
              </Table.Th>
              <Table.Th
                data-testid='hearingInfoResultThead-answer'
                data-testid-e2e='hearingInfoResultThead-answer'
                className='u-bg-gray-alt hearing-info-result__answer'
                key='回答'>
                回答
              </Table.Th>
            </Table.Tr>
          </Table.THead>
        </Table>
        <TableBodyContentData
          hearingInfoList={hearingInfoList}
          hearingId={hearingId}
          isLoading={isLoading}
          tableBodyClassName={tableBodyClassName}
        />
        <hr className='uc-divider' />
      </Modal.Content>
      <ModalAction onCloseModal={onCloseModal} gaSpotEvent={gaSpotEvent.cancel} />
    </Modal>
  );
};

interface ModalHeaderProps {
  code: string;
  firstInsDate: string | null;
  onClickCloseModal: () => void;
  gaSpotEventText: string;
}

export const ModalHeader = ({ code, firstInsDate, onClickCloseModal, gaSpotEventText }: ModalHeaderProps) => {
  useEffect(() => {
    document.querySelector('.uc-modal__close-button')?.setAttribute('onclick', gaSpotEventText);
  }, []);

  return (
    <Modal.Header onClickClose={onClickCloseModal}>
      <span className='u-flex u-text-m schedule-code' data-testid='scheduleCode' data-testid-e2e='scheduleCode'>
        案内番号 :<div className='u-text-bold u-text-l u-ml4'>{code}</div>
      </span>
      <div className='schedule-time u-text-s' data-testid='scheduleTime' data-testid-e2e='scheduleTime'>
        {`予約登録日時 : ${formatDateNull(firstInsDate || '', 'YYYY/MM/DD HH:mm:ss', 'YYYY年MM月DD日 HH:mm') || ''}`}
      </div>
    </Modal.Header>
  );
};

interface ModalActionProps {
  onCloseModal: () => void;
  gaSpotEvent: GASpotEventState;
}

export const ModalAction = ({ onCloseModal, gaSpotEvent }: ModalActionProps) => {
  useEffect(() => {
    const { className, spotEvent } = gaSpotEvent;
    document.querySelector(`.${className}`)?.setAttribute('onclick', spotEvent);
  }, []);

  return (
    <Modal.Actions className='u-mt12'>
      <div>
        <Button
          className={gaSpotEvent.className}
          label='閉じる'
          type='secondary'
          onClick={onCloseModal}
          data-testid='hearingResponseInfo-modal--cancel-button'
          data-testid-e2e='hearingResponseInfo-modal--cancel-button'
        />
      </div>
    </Modal.Actions>
  );
};

interface TableBodyContentDataProps {
  isLoading: boolean;
  hearingId: string | null;
  hearingInfoList: HearingInfo[];
  tableBodyClassName?: string;
}

export const TableBodyContentData = ({ hearingId, isLoading, hearingInfoList, tableBodyClassName }: TableBodyContentDataProps) => {
  if (!hearingId)
    return (
      <div className='u-flex u-justify-center is-no-result' data-testid='noExistHearingIdArea'>
        <div className='u-text-center no-result-text' data-testid='noExistHearingIdText'>
          未回答のため情報がありません。
        </div>
      </div>
    );
  if (isLoading)
    return (
      <div className='u-flex u-items-center u-justify-center is-no-result' data-testid='loadingArea'>
        <div className='u-text-center'>
          <CircularProgress isIndeterminate hasBackground={false} />
          <p data-testid='loadindText'>ページを読み込んでいます...</p>
        </div>
      </div>
    );
  if (!hearingInfoList.length)
    return (
      <div className='u-flex u-justify-center is-no-result' data-testid='dataBlankArea'>
        <div data-testid='dataBlankText' className='u-text-center no-result-text'>
          設問情報が取得できませんでした。
        </div>
      </div>
    );
  return (
    <div className={classNames('hearing-info-result__body', tableBodyClassName)}>
      {hearingInfoList.map((item, index) => (
        <div className='table-row u-flex u-items-center' key={`table-row-${index}`}>
          <Table.Td
            data-testid='hearingInfoResultTBody-question'
            className='hearing-info-result__question'
            data-testid-e2e='hearingInfoResultTBody-question'>
            {item.questionHeadline}
          </Table.Td>
          <Table.Td
            data-testid='hearingInfoResultTBody-answer'
            className='hearing-info-result__answer'
            data-testid-e2e='hearingInfoResultTBody-answer'>
            {item.answerName}
          </Table.Td>
        </div>
      ))}
    </div>
  );
};

export default HearingResponseInfoModal;
